import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, Zap, LogOut, ShoppingCart, Check, Clock, XCircle, Save, Infinity as InfinityIcon, Crown, Shield } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AccountModal({ open, onOpenChange, lang, theme, email, setEmail, onBuyMore, initialMode = "user" }) {
  const dark = theme === "dark";
  const ar = lang === "ar";
  const navigate = useNavigate();
  const [credits, setCredits] = useState(0);
  const [subscription, setSubscription] = useState(null);
  const [txns, setTxns] = useState([]);
  const [editing, setEditing] = useState(!email);
  const [draftEmail, setDraftEmail] = useState(email || "");
  // Admin login mode
  const [mode, setMode] = useState(initialMode); // "user" | "admin"
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [adminSubmitting, setAdminSubmitting] = useState(false);

  useEffect(() => {
    if (open && initialMode === "admin") setMode("admin");
  }, [open, initialMode]);

  useEffect(() => {
    if (!open || !email) return;
    axios.get(`${API}/user/credits`, { params: { email } }).then(r => {
      setCredits(r.data.remaining || 0);
      setSubscription(r.data.subscription && r.data.subscription.active ? r.data.subscription : null);
    }).catch(() => {});
    axios.get(`${API}/user/transactions`, { params: { email } }).then(r => setTxns(r.data.transactions || [])).catch(() => {});
    setDraftEmail(email);
    setEditing(false);
  }, [open, email]);

  const saveEmail = () => {
    const v = draftEmail.trim().toLowerCase();
    if (!v.includes("@") || v.length < 5) {
      toast.error(ar ? "أدخل بريدًا صحيحًا" : "Enter a valid email");
      return;
    }
    setEmail(v);
    localStorage.setItem("pl_email", v);
    setEditing(false);
    toast.success(ar ? "تم حفظ البريد" : "Email saved");
  };

  const signOut = () => {
    setEmail("");
    localStorage.removeItem("pl_email");
    setDraftEmail("");
    setCredits(0);
    setSubscription(null);
    setTxns([]);
    setEditing(true);
    toast.success(ar ? "تم تسجيل الخروج" : "Signed out");
    onOpenChange(false);
  };

  const adminLogin = async () => {
    const em = adminEmail.trim().toLowerCase();
    if (!em.includes("@") || adminPassword.length < 1) {
      toast.error(ar ? "أدخل بريد الإدارة وكلمة المرور" : "Enter admin email + password");
      return;
    }
    setAdminSubmitting(true);
    try {
      const r = await axios.post(`${API}/admin/login`, { email: em, password: adminPassword });
      sessionStorage.setItem("pl_admin_key", r.data.admin_key);
      toast.success(ar ? "دخول ناجح — جارٍ التحويل…" : "Login success — redirecting…");
      onOpenChange(false);
      setTimeout(() => navigate("/admin"), 400);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) toast.error(ar ? "بيانات الإدارة غير صحيحة" : "Invalid admin credentials");
      else toast.error(ar ? "فشل الدخول — حاول مرة أخرى" : "Login failed — try again");
    } finally {
      setAdminSubmitting(false);
    }
  };

  const dc = dark ? "bg-[#1f2a47] border-[#2e3a5c] text-white" : "bg-white text-[#2d2c35]";
  const boxCls = dark ? "bg-[#1f2a47] border-[#2e3a5c]" : "bg-[#f3f8ff] border-[#e5e5ea]";

  const statusIcon = (s) => {
    if (s === "paid") return <Check className="h-3.5 w-3.5 text-[#18c98f]" />;
    if (s === "pending" || s === "initiated") return <Clock className="h-3.5 w-3.5 text-yellow-400" />;
    return <XCircle className="h-3.5 w-3.5 text-red-400" />;
  };
  const statusLabel = (s) => ({
    paid:      ar ? "مدفوع"    : "Paid",
    pending:   ar ? "قيد الدفع" : "Pending",
    initiated: ar ? "قيد الدفع" : "Pending",
    failed:    ar ? "فشل"      : "Failed",
    declined:  ar ? "مرفوض"    : "Declined",
    cancelled: ar ? "ملغى"     : "Cancelled",
    abandoned: ar ? "متروك"    : "Abandoned",
    expired:   ar ? "منتهي"    : "Expired",
  }[s] || s);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="account-modal"
        className={`max-w-lg max-h-[90vh] overflow-y-auto ${dc}`}
        dir={ar ? "rtl" : "ltr"}
      >
        <DialogHeader>
          <DialogTitle className={`text-xl font-bold flex items-center gap-2 ${ar ? "font-arabic" : "font-outfit"}`}>
            {mode === "admin" ? (
              <>
                <Shield className="h-5 w-5 text-[#18c98f]" />
                {ar ? "دخول الإدارة" : "Admin login"}
              </>
            ) : (
              <>
                <Mail className="h-5 w-5 text-[#18c98f]" />
                {ar ? "حسابي" : "My Account"}
              </>
            )}
          </DialogTitle>
          <DialogDescription className={`${dark ? "text-white/70" : "text-[#2d2c35]/70"} ${ar ? "font-arabic" : "font-outfit"}`}>
            {mode === "admin"
              ? (ar ? "دخول خاص بفريق إدارة برولانسر فقط." : "Prolancer admin team only.")
              : (ar ? "بريدك الإلكتروني هو المعرّف الوحيد. رصيدك لا تنتهي صلاحيته." : "Your email is your only identifier. Credits never expire.")}
          </DialogDescription>
        </DialogHeader>

        {mode === "admin" ? (
          <div className="mt-3 space-y-3" data-testid="admin-login-panel">
            <div>
              <Label className={`${dark ? "text-white" : "text-[#2d2c35]"} ${ar ? "font-arabic" : "font-outfit"}`}>
                {ar ? "البريد الإلكتروني للإدارة" : "Admin email"}
              </Label>
              <Input
                data-testid="admin-login-email"
                type="email"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                placeholder="admin@prolancer.app"
                dir="ltr"
                className={`mt-2 h-10 ${boxCls} ${dark ? "text-white" : ""}`}
              />
            </div>
            <div>
              <Label className={`${dark ? "text-white" : "text-[#2d2c35]"} ${ar ? "font-arabic" : "font-outfit"}`}>
                {ar ? "كلمة المرور" : "Password"}
              </Label>
              <Input
                data-testid="admin-login-password"
                type="password"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") adminLogin(); }}
                placeholder="••••••••"
                dir="ltr"
                className={`mt-2 h-10 ${boxCls} ${dark ? "text-white" : ""}`}
              />
            </div>
            <Button
              data-testid="admin-login-submit"
              onClick={adminLogin}
              disabled={adminSubmitting}
              className="w-full h-11 bg-[#18c98f] hover:bg-[#12a777] text-[#1f2a47] font-extrabold rounded-full gap-2"
            >
              <Shield className="h-4 w-4" />
              {adminSubmitting ? (ar ? "جارٍ الدخول…" : "Signing in…") : (ar ? "دخول الإدارة" : "Admin sign in")}
            </Button>
            <button
              data-testid="admin-mode-back"
              onClick={() => setMode("user")}
              className={`w-full text-center text-xs ${dark ? "text-white/50 hover:text-white/80" : "text-[#2d2c35]/60 hover:text-[#2d2c35]"}`}
            >
              {ar ? "← عودة لحساب المستخدم" : "← Back to user account"}
            </button>
          </div>
        ) : (
          <>
            {/* Subscription banner — shows when active */}
            {subscription ? (
          <div
            data-testid="account-subscription-banner"
            className={`mt-2 rounded-2xl p-4 border ${dark ? "bg-[#18c98f]/10 border-[#18c98f]/40" : "bg-[#18c98f]/10 border-[#12a777]/40"}`}
          >
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl inline-flex items-center justify-center bg-[#18c98f] text-white">
                <Crown className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className={`text-[11px] uppercase tracking-widest font-bold text-[#18c98f]`}>
                  {ar ? "اشتراك نشط" : "Active subscription"}
                </div>
                <div className={`mt-0.5 font-bold ${ar ? "font-arabic" : "font-outfit"} ${dark ? "text-white" : "text-[#182138]"}`}>
                  {subscription.plan === "yearly_unlimited"
                    ? (ar ? "اشتراك سنوي غير محدود" : "Yearly unlimited")
                    : (ar ? "اشتراك شهري غير محدود" : "Monthly unlimited")}
                </div>
                <div className={`text-[11px] mt-0.5 ${dark ? "text-white/70" : "text-[#2d2c35]/70"} ${ar ? "font-arabic" : "font-outfit"}`}>
                  {ar
                    ? `يتبقّى ${subscription.days_remaining} يومًا`
                    : `${subscription.days_remaining} days remaining`}
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {/* Balance chip */}
        <div className={`mt-2 rounded-2xl p-4 border ${boxCls}`} data-testid="account-balance">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className={`text-[11px] uppercase tracking-widest ${dark ? "text-white/60" : "text-[#2d2c35]/60"}`}>
                {subscription ? (ar ? "اقتراحات AI" : "AI suggestions") : (ar ? "الرصيد المدفوع" : "Paid balance")}
              </div>
              <div className="flex items-baseline gap-2 mt-1">
                {subscription ? (
                  <>
                    <InfinityIcon className="h-6 w-6 text-[#18c98f]" />
                    <span
                      data-testid="account-credits-value"
                      className={`text-3xl font-extrabold ${ar ? "font-arabic" : "font-outfit"} ${dark ? "text-[#18c98f]" : "text-[#12a777]"}`}
                    >
                      {ar ? "لا محدود" : "Unlimited"}
                    </span>
                  </>
                ) : (
                  <>
                    <Zap className="h-5 w-5 text-[#18c98f]" />
                    <span className={`text-4xl font-bold font-mono-num ${dark ? "text-white" : "text-[#2d2c35]"}`} data-testid="account-credits-value">
                      {credits}
                    </span>
                    <span className={`text-sm ${dark ? "text-white/70" : "text-[#2d2c35]/70"}`}>
                      {ar ? "اقتراح" : "credits"}
                    </span>
                  </>
                )}
              </div>
            </div>
            <Button
              data-testid="account-buy-more"
              onClick={() => { onOpenChange(false); onBuyMore(); }}
              className="bg-gradient-to-r from-[#18c98f] to-[#12a777] hover:from-[#12a777] hover:to-[#0e8a62] text-[#1f2a47] font-bold rounded-full gap-2"
            >
              <ShoppingCart className="h-4 w-4" />
              {subscription ? (ar ? "تجديد" : "Renew") : (ar ? "شراء" : "Top up")}
            </Button>
          </div>
        </div>

        {/* Email */}
        <div className="mt-3">
          <Label className={`${dark ? "text-white" : "text-[#2d2c35]"} ${ar ? "font-arabic" : "font-outfit"}`}>
            {ar ? "البريد الإلكتروني (المعرّف)" : "Email (identifier)"}
          </Label>
          {editing ? (
            <div className="flex gap-2 mt-2">
              <Input
                data-testid="account-email-input"
                type="email"
                value={draftEmail}
                onChange={(e) => setDraftEmail(e.target.value)}
                placeholder="you@example.com"
                className={`h-10 ${boxCls} ${dark ? "text-white" : ""}`}
                dir="ltr"
              />
              <Button
                data-testid="account-save-email"
                onClick={saveEmail}
                className="bg-[#18c98f] hover:bg-[#12a777] text-[#1f2a47] font-bold gap-1"
              >
                <Save className="h-4 w-4" />
                {ar ? "حفظ" : "Save"}
              </Button>
            </div>
          ) : (
            <div className={`flex items-center justify-between gap-3 mt-2 rounded-xl border p-3 ${boxCls}`}>
              <span className="font-mono text-sm truncate" dir="ltr" data-testid="account-email-display">{email}</span>
              <div className="flex gap-2 shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="account-change-email"
                  onClick={() => setEditing(true)}
                  className={`h-8 ${dark ? "border-[#2e3a5c] bg-[#1f2a47] text-white hover:bg-[#2e3a5c]" : ""}`}
                >
                  {ar ? "تغيير" : "Change"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="account-signout"
                  onClick={signOut}
                  className={`h-8 gap-1 text-red-400 ${dark ? "border-red-500/40 bg-red-950/30 hover:bg-red-950/50" : "border-red-300 bg-red-50 hover:bg-red-100"}`}
                >
                  <LogOut className="h-3.5 w-3.5" />
                  {ar ? "خروج" : "Sign out"}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Transactions */}
        {email && (
          <div className="mt-4">
            <div className={`text-[11px] uppercase tracking-widest mb-2 ${dark ? "text-white/60" : "text-[#2d2c35]/60"}`}>
              {ar ? "آخر العمليات" : "Recent transactions"}
            </div>
            {txns.length === 0 ? (
              <div className={`rounded-xl p-4 text-center text-sm border ${boxCls} ${dark ? "text-white/60" : "text-[#2d2c35]/60"}`}>
                {ar ? "لا توجد عمليات بعد." : "No transactions yet."}
              </div>
            ) : (
              <div className={`rounded-xl border divide-y ${boxCls} ${dark ? "divide-[#2e3a5c]" : "divide-[#e5e5ea]"}`} data-testid="account-txn-list">
                {txns.map((t) => {
                  const isSub = t.package_type === "subscription";
                  const subLabel = t.plan === "yearly_unlimited"
                    ? (ar ? "اشتراك سنوي · غير محدود" : "Yearly · Unlimited")
                    : (ar ? "اشتراك شهري · غير محدود" : "Monthly · Unlimited");
                  return (
                    <div key={t.charge_id} className="flex items-center justify-between px-3 py-2.5 text-sm">
                      <div className="flex items-center gap-2 min-w-0">
                        {statusIcon(t.payment_status)}
                        <div className="min-w-0">
                          <div className={`font-medium truncate ${dark ? "text-white" : "text-[#2d2c35]"}`}>
                            {isSub ? subLabel : `${t.credits} ${ar ? "اقتراح" : "credits"}`}
                          </div>
                          <div className={`text-[11px] ${dark ? "text-white/50" : "text-[#2d2c35]/50"}`} dir="ltr">
                            {(t.created_at || "").slice(0, 10)} · {statusLabel(t.payment_status)}
                          </div>
                        </div>
                      </div>
                      <div className={`text-sm font-mono-num font-semibold shrink-0 ${dark ? "text-white" : "text-[#2d2c35]"}`}>
                        {t.amount} {t.currency || "SAR"}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

            {/* Admin login footer — team-only entry point */}
            <button
              data-testid="admin-mode-open"
              onClick={() => setMode("admin")}
              className={`mt-5 w-full rounded-xl p-3 inline-flex items-center justify-center gap-2 text-xs font-bold border transition ${
                dark
                  ? "bg-[#182138] border-[#2e3a5c] text-white/70 hover:border-[#18c98f] hover:text-[#18c98f]"
                  : "bg-[#f3f8ff] border-[#e5e5ea] text-[#2d2c35]/70 hover:border-[#12a777] hover:text-[#12a777]"
              }`}
            >
              <Shield className="h-3.5 w-3.5" />
              {ar ? "دخول الإدارة (للفريق فقط)" : "Admin login (team only)"}
            </button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
